import { FormEvent, ReactNode, useEffect, useState } from 'react'
import { api } from './api'
import { Link } from './navigation'
import { Notice } from './ui'
import './system-navigation.css'
type Operator = { principal_id: string; display_name: string; role: string; email: string }
const request=<T,>(path:string,init:RequestInit={})=>api<T>(path,{...init,signal:AbortSignal.timeout(20000)})
export function SystemLayout({active,children}:{active:'overview'|'companies'|'company-setup'|'people'|'apps'|'sessions'|'connections'|'google-setup'|'google-drive'|'folder-access'|'admins'|'activity';children:ReactNode}) {
 const googleActive=active==='connections'||active==='google-setup'||active==='google-drive'||active==='folder-access'
 const [googleOpen,setGoogleOpen]=useState(googleActive)
 useEffect(()=>{if(googleActive)setGoogleOpen(true)},[googleActive,active])
 return <><div className="system-banner" role="note"><strong>System administration</strong><span>Changes here apply to every company on this AuthEM system.</span></div><div className="system-layout"><aside className="system-sidebar"><p className="eyebrow">System</p><nav className="system-rail" aria-label="Admin pages">
  <ul className="system-nav-tree">
   <li><Link to="/system/overview" aria-current={active==='overview'?'page':undefined}>Overview</Link></li>
   <li><Link to="/system/companies" aria-current={active==='companies'?'page':undefined}>Companies</Link></li>
   <li><Link to="/system/people" aria-current={active==='people'?'page':undefined}>People</Link></li>
   <li><Link to="/system/apps" aria-current={active==='apps'?'page':undefined}>Apps</Link></li>
   <li><Link to="/system/company-setup" aria-current={active==='company-setup'?'page':undefined}>Company setup</Link></li>
   <li><button type="button" className="system-nav-group" aria-expanded={googleOpen} aria-controls="system-google-branch" onClick={()=>setGoogleOpen(!googleOpen)}><span aria-hidden="true">{googleOpen?'▾':'▸'}</span> Google</button>
    <ul id="system-google-branch" hidden={!googleOpen}>
     <li><Link to="/system/google-setup" aria-current={active==='google-setup'?'page':undefined}>Registration</Link></li>
     <li><Link to="/system/google-drive" aria-current={active==='google-drive'||active==='connections'?'page':undefined}>Drive</Link></li>
     <li><Link to="/system/folder-access" aria-current={active==='folder-access'?'page':undefined}>Team folders</Link></li>
    </ul>
   </li>
   <li><Link to="/system/admins" aria-current={active==='admins'?'page':undefined}>Manage admins</Link></li>
   <li><Link to="/system/sessions" aria-current={active==='sessions'?'page':undefined}>Sessions</Link></li>
   <li><Link to="/system/activity" aria-current={active==='activity'?'page':undefined}>Activity</Link></li>
  </ul>
 </nav></aside><div className="system-main">{children}</div></div></>
}
const roleName = (role: string) => role === 'superadmin' ? 'Admin' : 'Read-only admin'
type Pending = { kind: 'role'; operator: Operator; role: string } | { kind: 'remove'; operator: Operator } | { kind: 'add'; email: string; role: string }

export function SystemAdmins() {
  const [items, setItems] = useState<Operator[]>([])
  const [self, setSelf] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('superadmin')
  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    setLoadError('')
    try { setItems(await request<Operator[]>('/api/v1/system/operators')) } catch { setLoadError('We could not load the admin list. Try again.') } finally { setLoading(false) }
  }
  useEffect(() => {
    void load()
    request<{ principal_id?: string }>('/api/v1/session').then(session => setSelf(session.principal_id ?? '')).catch(() => setSelf(''))
  }, [])

  const confirm = async () => {
    if (!pending) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      if (pending.kind === 'add') {
        await request('/api/v1/system/operators', { method: 'POST', body: JSON.stringify({ email: pending.email, role: pending.role }) })
        setEmail('')
        setRole('superadmin')
        setNotice('Admin added. They can sign in with their own account.')
      } else if (pending.kind === 'role') {
        await request(`/api/v1/system/operators/${encodeURIComponent(pending.operator.principal_id)}`, { method: 'PATCH', body: JSON.stringify({ role: pending.role }) })
        setNotice(`${pending.operator.display_name || pending.operator.email} is now ${roleName(pending.role).toLowerCase()}.`)
      } else {
        await request(`/api/v1/system/operators/${encodeURIComponent(pending.operator.principal_id)}`, { method: 'DELETE' })
        setNotice(`Admin access removed for ${pending.operator.display_name || pending.operator.email}. Their account is unchanged.`)
      }
      setPending(null)
      await load()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'This change could not be made. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const who = (operator: Operator) => operator.display_name || operator.email || 'this admin'
  const question = !pending ? '' : pending.kind === 'add'
    ? `Make ${pending.email} ${roleName(pending.role) === 'Admin' ? 'an admin' : 'a read-only admin'}? ${pending.role === 'superadmin' ? 'They will be able to manage this whole AuthEM system.' : 'They will be able to view, but not change, system settings.'}`
    : pending.kind === 'role'
      ? `Change ${who(pending.operator)}${pending.operator.principal_id === self ? ' (you)' : ''} to ${roleName(pending.role).toLowerCase()}?${pending.role === 'auditor' && pending.operator.principal_id === self ? ' You will lose the ability to change system settings.' : ''}`
      : `Remove admin access for ${who(pending.operator)}${pending.operator.principal_id === self ? ' (you)' : ''}? Their AuthEM account stays; any admin API tokens they hold stop working.`

  const confirmation = pending && <div className="admin-confirm" role="alertdialog" aria-label="Confirm admin change">
    <p>{question}</p>
    {error && <Notice tone="error">{error}</Notice>}
    <div className="admin-confirm-actions">
      <button className={`button ${pending.kind === 'remove' ? 'danger' : 'primary'}`} disabled={busy} onClick={() => { void confirm() }}>{busy ? 'Saving…' : pending.kind === 'remove' ? 'Remove access' : 'Confirm'}</button>
      <button className="button secondary" disabled={busy} onClick={() => { setPending(null); setError('') }}>Cancel</button>
    </div>
  </div>

  return <SystemLayout active="admins">
    <div className="page-heading"><div><h1>Manage admins</h1><p>Admins manage this whole AuthEM system. Read-only admins can look but not change anything.</p></div></div>
    {notice && <Notice tone="success">{notice}</Notice>}
    <section className="section-card">
      <h2>Current admins</h2>
      {loading ? <p role="status">Loading admins…</p> : items.length ? <div className="admin-list">{items.map(item => <article className="admin-row" key={item.principal_id}>
        <div><strong>{item.display_name || item.email || 'Admin'}{item.principal_id === self && ' (you)'}</strong><p>{item.email || 'No verified email on this account'}</p></div>
        <div className="admin-row-actions">
          <label className="visually-hidden" htmlFor={`role-${item.principal_id}`}>Role for {who(item)}</label>
          <select id={`role-${item.principal_id}`} value={item.role} disabled={busy || pending !== null} onChange={event => { setError(''); setPending({ kind: 'role', operator: item, role: event.target.value }) }}>
            <option value="superadmin">Admin</option>
            <option value="auditor">Read-only admin</option>
          </select>
          <button className="button secondary" disabled={busy || pending !== null} onClick={() => { setError(''); setPending({ kind: 'remove', operator: item }) }}>Remove</button>
        </div>
      </article>)}</div> : !loadError && <p>No admins to show.</p>}
      {loadError && <Notice tone="error">{loadError}</Notice>}
      {!loading && loadError && <button className="button secondary" onClick={() => { void load() }}>Try again</button>}
      {pending && pending.kind !== 'add' && confirmation}
    </section>
    <section className="section-card">
      <h2>Add an admin</h2>
      <p>Enter the email they use for AuthEM. They must already have an account and have verified this email.</p>
      <form className="company-settings-form" onSubmit={(event: FormEvent) => { event.preventDefault(); setError(''); setPending({ kind: 'add', email: email.trim(), role }) }}>
        <label>Email address<input type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="name@company.com" required disabled={busy || pending !== null} /></label>
        <label>Role<select value={role} onChange={event => setRole(event.target.value)} disabled={busy || pending !== null}><option value="superadmin">Admin</option><option value="auditor">Read-only admin</option></select></label>
        <button className="button primary" disabled={busy || pending !== null || !email.trim()}>Add admin</button>
      </form>
      {pending && pending.kind === 'add' && confirmation}
    </section>
  </SystemLayout>
}
